import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { MedicalNote, SessionFormField } from '@uplift/api';
import { NoteSection } from '../components/NoteSection';
import { ServerForm } from '@uplift/ui-v2';
import { DiagnosisSelect } from '../../../components';
import { Diagnosis } from '@uplift/types';
import { useEffect, useMemo } from 'react';
import { useFormikContext } from 'formik';

export type DischargeNoteFormProps = {
  formFields: MedicalNote['form']['fields'];
  patientFullName: string;
  diagnoses: Diagnosis[];
};

type DischargeNoteFieldGroupId =
    | 'discharge_reason_group'
    | 'prognosis_group'
    | 'discharge_summary_group';

const dischargeNoteFieldGroupIds: DischargeNoteFieldGroupId[] = [ 'discharge_reason_group', 'prognosis_group', 'discharge_summary_group' ];

export const DischargeNoteForm: React.VFC<DischargeNoteFormProps> = props => {
  const formik = useFormikContext();
  const { classes } = useStyles();

  const fieldGroupHash = dischargeNoteFieldGroupIds.reduce((prev, fieldGroupId) => {
    const fieldGroup = props.formFields.find(f => f.id === fieldGroupId);

    return { ...prev, [fieldGroupId]: fieldGroup?.fieldType === 'fields_group' ? fieldGroup.formFields : [] };
  }, {} as Record<DischargeNoteFieldGroupId, SessionFormField[]>);


  // custom value check
  const clientDidNotStartTreatment = useMemo(() => {
    return formik.values['reason_for_discharge'] === 'Client did not start treatment';
  }, [ formik.values ]);

  const setValues = formik.setValues;

  useEffect(() => {
    if (clientDidNotStartTreatment) {


      const defaults = {
        prognosis: fieldGroupHash.prognosis_group.find(field => field.id === 'prognosis').defaultValue ?? null,
        continuing_care_recommendations: fieldGroupHash.discharge_summary_group.find(field => field.id === 'continuing_care_recommendations').defaultValue ?? null,
      };

      setValues((values) => ({
        ...values,
        diagnosis: [],
        ...defaults
      }));
    }
  }, [ clientDidNotStartTreatment, setValues ]);

  return (
    <Box display="flex" flexDirection="column" gap="24px" mb={8}>
      <NoteSection title={props.patientFullName} />
      <NoteSection title="Reason for discharge" className={classes.select}>
        <ServerForm fields={fieldGroupHash.discharge_reason_group} />
      </NoteSection>
      <NoteSection title="Discharge Diagnosis" disabled={clientDidNotStartTreatment}>
        <Box px={3}>
          <DiagnosisSelect
            name="diagnosis"
            diagnoses={props.diagnoses}
            color="secondary"
            disabled={clientDidNotStartTreatment}
          />
        </Box>
      </NoteSection>
      <NoteSection title="Prognosis" disabled={clientDidNotStartTreatment}>
        <ServerForm fields={fieldGroupHash.prognosis_group} />
      </NoteSection>
      <NoteSection title="Discharge Summary">
        <ServerForm fields={fieldGroupHash.discharge_summary_group} />
      </NoteSection>
    </Box>
  );
};

const useStyles = makeStyles()(() => ({
  select: {
    maxWidth: '564px',
    width: '100%'
  }
}));
